<script setup lang="ts">
const app = useNuxtApp()
const route = useRoute()
const storeIndex = useStoreIndex()
const storeArticleList = useStoreArticleList()

definePageMeta({ layout: 'layout-side-default' })

const pageData = {
  items: [] as any[],
  loading: false,
  termList: [] as Article[],
}

const modelSlug = ref(route.params.slug)

const setHead = () => {
  const baseTitle = '皇室がご覧になった展覧会'
  const headDescription = `天皇皇后両陛下、秋篠宮家、上皇上皇后両陛下がご覧になった、美術館・博物館の展覧会を紹介。過去のアーカイブも掲載しています。`
  const headInput = app.$makeMeta({
    title: baseTitle,
    description: headDescription,
  })

  storeIndex.setBreadCrumbs([
    {
      text: baseTitle,
      disabled: true,
    },
  ])

  useHead(headInput)
}

const onChange = (val) => {
  navigateTo(`/royal-family/${val}`)
}

const initPage = async () => {
  const [_resSite, _resMaster, resMain] = await Promise.all([
    storeIndex.fetchSite(),
    storeIndex.fetchMaster(),
    storeArticleList.fetchList('royalFamilyMain'),
  ])
  if (resMain.code !== 200000) {
    throw createError({ statusCode: 1002 })
  }
  const rfList = resMain.data.articles as Article[]
  if (rfList.length === 0) {
    return
  }
  pageData.termList = rfList

  // slug = 期間slug
  const slug = modelSlug.value
  let item = null
  //  slugの記事を探す
  if (slug) {
    //  記事をループ
    rfList.some((a) => {
      if (slug === a.article_slug) {
        item = a
        return true
      }
    })
  } else {
    item = rfList[0]
  }

  if (!item) {
    return
  }
  let bodyJson
  try {
    bodyJson = JSON.parse(item.body_json)
  } catch (e) {
    return
  }
  //  jsonループ整形＆event を探して、slug配列を作成
  const hashLoadEvents = {}
  if (bodyJson.loop?.length && !Array.isArray(bodyJson.loop[0])) {
    for (let i = 0; i < bodyJson.loop.length; i++) {
      const visit = bodyJson.loop[i]
      //  p-eventがあるか
      const event = visit['p-event']
      if (event?.value) {
        const ary = event.value.split(',')
        if (ary?.length === 3 && ary[0] === 'event') {
          visit.eventSlug = ary[1]
          visit.eventTitle = ary[2]

          hashLoadEvents[visit.eventSlug] = {
            slug: visit.eventSlug,
            museum: {},
          }
        }
      }
      const pDate = visit['p-date']
      if (pDate?.value) {
        visit.date = pDate.value
      } else {
        visit.date = ''
      }
      const pKoushitsu = visit['p-koushitsu']
      if (pKoushitsu?.value) {
        visit.koushitsu = pKoushitsu.value
        delete visit['p-koushitsu']
      } else {
        visit.koushitsu = ''
      }
    }
    //  日付でソート
    bodyJson.loop = bodyJson.loop.sort((a, b) => {
      if (useDayjs(a.date).isBefore(b.date)) {
        return 1
      } else {
        return -1
      }
    })

    //  イベント読み込み
    const aryEvents = Object.keys(hashLoadEvents)
    if (aryEvents.length > 0) {
      const resEvents = await storeArticleList.fetchList('royalFamilyEvent', { query: { article_slug: aryEvents.join(',') } })
      console.log('resEvents:', aryEvents, resEvents)
      if (resEvents.code === 200000) {
        const articles = resEvents.data.articles as Article[]
        console.log('resEvents.data.articles:', articles)
        for (let e = 0; e < articles.length; e++) {
          const eventItem = articles[e]
          //  レポートがあるかどうか
          const reportItems = app.$getArticleRelationsMulti(eventItem, 'report', null)
          let reportUserSlug = null
          let reportImSlug = null
          for (let i = 0; i < reportItems.length; i++) {
            const reportItem = reportItems[i]
            if (reportItem.custom.is_im_report === '1' || reportItem.custom.is_im_report === 1) {
              reportImSlug = reportItem.article_slug
            } else {
              reportUserSlug = reportItem.article_slug
            }
          }

          hashLoadEvents[eventItem.article_slug] = {
            slug: eventItem.article_slug,
            reportUserSlug: reportUserSlug,
            reportImSlug: reportImSlug,
            museum: {
              slug: eventItem.custom.museum_article_slug,
              title: eventItem.custom.museum_title,
            },
          }
        }
      }
    }

    //  もう一回ループしてイベント名とmuseum情報を整理
    for (let i = 0; i < bodyJson.loop.length; i++) {
      const visit = bodyJson.loop[i]
      visit.museumTitle = ''
      visit.museumSlug = ''
      visit.reportUserSlug = ''
      visit.reportImSlug = ''
      // event詳細が読み込めたか？
      if (visit.eventSlug) {
        const event = hashLoadEvents[visit.eventSlug]
        if (event) {
          visit.museumTitle = event.museum.title
          visit.museumSlug = event.museum.slug
          visit.reportUserSlug = event.reportUserSlug
          visit.reportImSlug = event.reportImSlug
        }
      }
      if (visit.museumTitle === '') {
        // p-other_nameがあればそれを使う
        const otherName = visit['p-other_name']
        if (otherName) {
          visit.eventTitle = otherName.value
          visit.eventSlug = ''
        }
        // p-other_museumがあればそれを使う
        const otherMuseum = visit['p-other_museum']
        if (otherMuseum) {
          visit.museumTitle = otherMuseum.value
        }
      }
      if (visit.date) {
        visit.date = app.$getPeriodDayLabel(visit.date)
      }
    }

    pageData.items = bodyJson.loop
  }
}
await initPage()
setHead()
</script>

<template>
  <div class="p-articlePage">
    <h2 class="c-heading_page">
      皇室がご覧になった展覧会
    </h2>
    <p class="c-heading_description">
      天皇皇后両陛下、秋篠宮家、上皇上皇后両陛下がご覧になった展覧会をご紹介します。
    </p>

    <div class="p-searchPanel">
      <div class="p-searchPanel_row nowrap">
        <div class="p-searchPanel_item">
          <v-select
            v-model="modelSlug"
            class="c-searchControl"
            v-bind:items="pageData.termList"
            item-title="title"
            item-value="article_slug"
            label="年"
            color="primary"
            variant="underlined"
            single-line
            hide-details
            v-bind:disabled="pageData.loading"
            v-on:update:model-value="onChange"
          />
        </div>
      </div>
    </div>

    <div class="p-articlePage_result">
      <template
        v-if="pageData.items.length === 0"
      >
        <div
          class="p-articlePage_result_noItem"
        >
          情報が入り次第、更新いたします。
        </div>
      </template>
      <template
        v-else
      >
        <div
          v-for="(item, index) in pageData.items"
          v-bind:key="item.museumTitle + index"
          class="c-royalFamilyItem"
        >
          <div class="c-royalFamilyItem_left">
            <div class="c-royalFamilyItem_duration">
              {{ item.date }}
            </div>
            <div class="c-royalFamilyItem_name">
              {{ item.koushitsu }}
            </div>
            <div class="c-royalFamilyItem_title">
              <template
                v-if="item.eventSlug"
              >
                <nuxt-link
                  v-bind:to="`/event/${item.eventSlug}`"
                >
                  {{ item.eventTitle }}
                </nuxt-link>
              </template>
              <template
                v-else
              >
                {{ item.eventTitle }}
              </template>
            </div>
            <div class="c-royalFamilyItem_museum">
              <template
                v-if="item.museumSlug"
              >
                <nuxt-link
                  v-bind:to="`/museum/${item.museumSlug}`"
                >
                  {{ item.museumTitle }}
                </nuxt-link>
              </template>
              <template
                v-else
              >
                {{ item.museumTitle }}
              </template>
            </div>
            <div class="c-royalFamilyItem_tags">
              <div
                v-if="item.reportUserSlug"
                class="c-royalFamilyItem_tags_item -userReport"
              >
                <nuxt-link
                  v-bind:to="`/report/${item.reportUserSlug}`"
                >
                  読者レポート
                </nuxt-link>
              </div>
              <div
                v-if="item.reportImSlug"
                class="c-royalFamilyItem_tags_item -imReport"
              >
                <nuxt-link
                  v-bind:to="`/report/${item.reportImSlug}`"
                >
                  IMレポート
                </nuxt-link>
              </div>
              <div
                v-if="item.eventSlug"
                class="c-royalFamilyItem_tags_item -exInfo"
              >
                <nuxt-link
                  v-bind:to="`/event/${item.eventSlug}`"
                >
                  展覧会情報
                </nuxt-link>
              </div>
            </div>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@use "@/assets/css/foundation/mixin/responsive";

.p-articlePage_result_noItem {
  font-size: 16px;
}

.p-searchPanel {
  // margin: 40px 0;
  margin-top: 24px;
  padding: 0 10px 19px;
  border-width: 1px;
  border-style: solid;
  border-color: #014da3;
  background-color: #f1f4f6;

  &_row {
    display: flex;
    flex-wrap: wrap;

    &.nowrap {
      flex-wrap: nowrap;

      @include responsive.mq_down(sp) {
        flex-wrap: wrap;
      }
    }
  }

  &_item {
    flex-grow: 1;
    min-width: 25%;
    margin: 17px 0 5px;
    padding: 0 10px;
  }
}

.c-royalFamilyItem {
  display: flex;
  padding: 17px 0 30px;
  border-bottom: 1px solid rgb(0, 0, 0, 0.12);

  @include responsive.mq_down(sp) {
    display: block;
  }

  &_left {
    flex: 1;
  }

  &_right {
    flex: 0 0 220px;
    flex-basis: 220px;
    max-width: 220px;

    @include responsive.mq_down(sp) {
      max-width: 100%;
      margin-top: 20px;
    }
  }

  &_duration {
    color: var(--g_color_text_888888);
    font-size: 14px;
    font-weight: lighter;
  }

  &_nameRow {
    display: flex;
    align-items: center;
  }

  &_name,
  &_title {
    font-size: 20px;
    font-weight: bold;

    @include responsive.mq_down(sp) {
      font-size: 18px;
    }

    a {
      color: inherit;
      font-weight: bold;
    }
  }

  &_museum {
    margin-top: 8px;
    font-size: 14px;
    font-weight: bold;

    a {
      color: inherit;
      font-weight: bold;
    }
  }

  &_tags {
    display: flex;
    flex-wrap: wrap;

    &_item {
      min-width: 100px;
      margin-top: 17px;
      margin-right: 20px;
      padding: 2px;
      background-color: rgb(0, 78, 162);
      color: #fff;
      font-size: 14px;
      font-weight: bold;
      text-align: center;

      > a {
        display: block;
        width: 100%;
        height: 100%;
        color: #fff;
        font-weight: bold;

        &:hover {
          text-decoration: none;
        }
      }

      &:hover {
        opacity: 0.7;
      }

      &.-userReport {
        background-color: var(--color_report_author_bg_user);
      }

      &.-imReport {
        background-color: var(--color_report_author_bg_im);
      }

      &.-exInfo {
        background-color: rgb(182, 145, 1);
      }
    }
  }

  &_images {
    display: flex;
    justify-content: flex-end;
    text-align: center;

    @include responsive.mq_down(sp) {
      justify-content: center;
    }

    &_item {
      flex: 0 0 50%;
      margin-left: 4px;

      @include responsive.mq_down(sp) {
        flex: inherit;
      }

      &:first-of-type {
        margin-left: 0;
      }

      a {
        display: block;

        &:hover {
          opacity: 0.8;
        }
      }

      img {
        width: 100%;
        height: 120px;
        object-fit: contain;
      }

      .caption {
        margin-top: 5px;
      }
    }
  }
}
</style>
